import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryCreator } from "redux/baseQuery";
import {
  BulkPaymentItemListResponse,
  BulkPaymentItemRequest,
  DatasetDownloadResponse,
  FileDetailsReponse,
} from "./types";
import { DataLoaderDataSets } from "common";
import env from "env";

const { dataLoaderService } = env.api.baseUrl;

const baseQuery = fetchBaseQuery({
  baseUrl: dataLoaderService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const getMetaData = (
  datasetName: string,
  organizationNumber: string | undefined,
  orgId: string | undefined
) => {
  switch (datasetName) {
    case DataLoaderDataSets.DAKKO_PAYSLIP_DS:
      return { CompanyCode: organizationNumber };
    case DataLoaderDataSets.PAYROLL_DS:
      return { orgId };
    default:
      break;
  }
};

export const dataLoaderApi = createApi({
  reducerPath: "dataLoaderApi",
  baseQuery: baseQueryCreator(baseQuery),
  tagTypes: ["requestDetails"],
  endpoints: (builder) => ({
    uploadData: builder.mutation<
      void,
      {
        inputFile?: File;
        organisationNumber?: string;
        datasetName?: string;
        orgId?: string;
        uploadType?: string;
        documentId?: string;
        entityId?: string;
        orgToken?: string;
      }
    >({
      query: ({
        inputFile,
        organisationNumber,
        datasetName = DataLoaderDataSets.DAKKO_PAYSLIP_DS,
        orgId,
        uploadType = "RDB",
        documentId,
        entityId,
        orgToken,
      }) => {
        const formData = new FormData();
        if (inputFile) formData.append("inputFile", inputFile);
        if (uploadType) formData.append("uploadType", uploadType);
        if (documentId) formData.append("documentId", documentId);
        if (entityId) formData.append("entityId", entityId);

        formData.append("datasetName", datasetName);
        formData.append(
          "metaData",
          JSON.stringify(getMetaData(datasetName, organisationNumber, orgId))
        );
        formData.append("validateDataFileOnly", "true");

        const headers: Record<string, string> = {};
        if (orgToken) {
          headers["org-token"] = orgToken;
        }

        return {
          url: "/files",
          method: "POST",
          body: formData,
          headers,
        };
      },
    }),
    getBulkPaymentData: builder.query<
      BulkPaymentItemListResponse,
      BulkPaymentItemRequest
    >({
      query: (params) => ({
        url: "datasets/upload/PAYROLL_DS/history",
        params: {
          ...params,
        },
      }),
    }),
    downloadFile: builder.mutation<DatasetDownloadResponse, string>({
      query: (id) => {
        return {
          url: `datasets/download/${id}/results`,
          method: "GET",
        };
      },
    }),
    getRequestDetails: builder.query<FileDetailsReponse, string>({
      query: (requestId) => {
        return {
          url: `/files/${requestId}`,
          method: "GET",
        };
      },
      providesTags: ["requestDetails"],
      keepUnusedDataFor: 2,
    }),
  }),
});

export const {
  useUploadDataMutation,
  useGetBulkPaymentDataQuery,
  useDownloadFileMutation,
  useGetRequestDetailsQuery,
} = dataLoaderApi;
